import { getRange } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";

export type EmployeeRangeParams = { from?: string; to?: string; date?: string };

/**
 * The employee pages that carry a date picker fall into two persistence groups:
 *
 * - "month": a single, globally shared month. Every date-aware page in the "all
 *   employees" overview uses it, as do a single employee's Arbeitszeiten and
 *   Vorschüsse pages. Picking a month on any of them carries over to all the
 *   others, regardless of whether we're looking at all employees or just one.
 * - "history": a single employee's free range, defaulting to that employee's
 *   entire contract history. Shared between their Arbeitszeitkonto, Urlaubskonto,
 *   Zuschlagskonto, Abwesenheiten and Verfügbarkeiten pages and kept separate
 *   from the shared month.
 */
export type EmployeeRangeSlot = "month" | "history";

export type EmployeeRangeScope = "all" | "single";

const slotsByScopeAndPage: Record<EmployeeRangeScope, Record<string, EmployeeRangeSlot>> = {
    all: {
        time: "month",
        "time-balance": "month",
        "vacation-balance": "month",
        "bonuses-balance": "month",
        absences: "month",
        availabilities: "month",
        advances: "month",
    },
    single: {
        time: "month",
        advances: "month",
        "time-balance": "history",
        "vacation-balance": "history",
        "bonuses-balance": "history",
        absences: "history",
        availabilities: "history",
    },
};

export function getEmployeeRangeSlot(scope: EmployeeRangeScope, page: string): EmployeeRangeSlot | undefined {
    return slotsByScopeAndPage[scope]?.[page];
}

const rangeMemory = new Map<EmployeeRangeSlot, EmployeeRangeParams>();

export function rememberEmployeeRange(slot: EmployeeRangeSlot, params: EmployeeRangeParams) {
    const normalized = normalizeRange(params);
    if (!normalized) {
        return;
    }
    rangeMemory.set(slot, normalized);
}

export function getRememberedEmployeeRange(slot: EmployeeRangeSlot): EmployeeRangeParams | undefined {
    return rangeMemory.get(slot);
}

/**
 * Pages disagree on how they express the selected period: most use a `from`/`to`
 * range, while Abwesenheiten and Verfügbarkeiten use a single `date` (the month).
 * To let a month picked on one shape apply to the other we always store both
 * representations together, derived from whichever the page provided.
 */
export function normalizeRange({ from, to, date }: EmployeeRangeParams): EmployeeRangeParams | undefined {
    if (from && to) {
        return { from, to, date: date ?? from };
    }
    if (date) {
        const { from: monthFrom, to: monthTo } = getRange(date as DateString, "month");
        return { from: monthFrom, to: monthTo, date };
    }
    return undefined;
}
